import type { DataNode } from 'antd/es/tree';

export interface RoleMenuTreeSelection {
  checkedKeys: string[];
  halfCheckedKeys: string[];
}

/**
 * 将后端持久化的菜单 ID 转成 Ant Tree 的选中态。
 *
 * 父菜单本身也是后端构建用户菜单树所需的授权记录，不能只依赖 Tree 的
 * 勾选传导来推导。尤其当父菜单的所有叶子都已授权时，父菜单必须进入
 * checkedKeys；部分授权时则放进 halfCheckedKeys，确保再次保存不会丢父节点。
 */
export const splitMenuKeysForTree = (
  menuIds: string[],
  nodes: DataNode[],
): RoleMenuTreeSelection => {
  const assignedKeys = new Set(menuIds.map(String));
  const checked = new Set<string>();
  const halfChecked = new Set<string>();

  const traverse = (node: DataNode): { totalLeaves: number; selectedLeaves: number } => {
    const key = String(node.key);
    const children = node.children || [];

    if (children.length === 0) {
      if (assignedKeys.has(key)) {
        checked.add(key);
        return { totalLeaves: 1, selectedLeaves: 1 };
      }
      return { totalLeaves: 1, selectedLeaves: 0 };
    }

    const childState = children.reduce(
      (acc, child) => {
        const state = traverse(child);
        return {
          totalLeaves: acc.totalLeaves + state.totalLeaves,
          selectedLeaves: acc.selectedLeaves + state.selectedLeaves,
        };
      },
      { totalLeaves: 0, selectedLeaves: 0 },
    );

    if (assignedKeys.has(key)) {
      if (childState.totalLeaves > 0 && childState.selectedLeaves === childState.totalLeaves) {
        checked.add(key);
      } else {
        halfChecked.add(key);
      }
    }

    return childState;
  };

  nodes.forEach(traverse);

  return {
    checkedKeys: Array.from(checked),
    halfCheckedKeys: Array.from(halfChecked),
  };
};

export const buildRoleMenuIDs = (checkedKeys: string[], halfCheckedKeys: string[]): number[] =>
  Array.from(new Set([...checkedKeys, ...halfCheckedKeys])).map(Number);
